/**
 * Webhook Service
 *
 * Frontend service for managing event-notification webhooks.
 * Communicates with the backend `/webhooks` routes.
 */

import { apiRequest } from './api';
import type {
  Webhook,
  WebhookDelivery,
  CreateWebhookRequest,
  UpdateWebhookRequest,
} from '@simcheck/shared-types';

// ============================================================================
// Types
// ============================================================================

/** Webhook as returned by listings/detail — never includes the secret. */
export type WebhookListItem = Omit<Webhook, 'secret'>;

export interface WebhookListResponse {
  webhooks: WebhookListItem[];
  count: number;
}

export interface WebhookCreateResponse {
  webhook: {
    id: string;
    name: string;
    description?: string;
    url: string;
    events: Webhook['events'];
    status: Webhook['status'];
    createdAt: string;
  };
  secret: string; // Signing secret — ONLY returned once, at creation.
  message: string;
}

export interface WebhookUpdateResponse {
  message: string;
  webhook: WebhookListItem;
}

export interface WebhookTestResponse {
  success: boolean;
  responseStatus?: number;
  responseTime?: number;
  error?: string;
}

export interface WebhookDeliveriesResponse {
  deliveries: WebhookDelivery[];
  count: number;
}

export interface WebhookRegenerateSecretResponse {
  secret: string;
  message: string;
}

// ============================================================================
// Service Functions
// ============================================================================

/** List all webhooks for the current organization. */
export async function listWebhooks(organizationId: string): Promise<WebhookListResponse> {
  return apiRequest<WebhookListResponse>('/webhooks', {}, organizationId);
}

/** Get a single webhook by ID (secret never included). */
export async function getWebhook(id: string, organizationId: string): Promise<WebhookListItem> {
  return apiRequest<WebhookListItem>(`/webhooks/${id}`, {}, organizationId);
}

/**
 * Create a new webhook.
 * Returns the signing secret — SAVE IT, this is the only time it's shown.
 */
export async function createWebhook(
  request: CreateWebhookRequest,
  organizationId: string
): Promise<WebhookCreateResponse> {
  return apiRequest<WebhookCreateResponse>(
    '/webhooks',
    {
      method: 'POST',
      body: JSON.stringify(request),
    },
    organizationId
  );
}

/** Update a webhook's configuration (name, url, events, status, …). */
export async function updateWebhook(
  id: string,
  request: UpdateWebhookRequest,
  organizationId: string
): Promise<WebhookUpdateResponse> {
  return apiRequest<WebhookUpdateResponse>(
    `/webhooks/${id}`,
    {
      method: 'PUT',
      body: JSON.stringify(request),
    },
    organizationId
  );
}

/** Permanently delete a webhook. */
export async function deleteWebhook(
  id: string,
  organizationId: string
): Promise<{ message: string; webhookId: string }> {
  return apiRequest(
    `/webhooks/${id}`,
    {
      method: 'DELETE',
    },
    organizationId
  );
}

/** Send a synthetic test event to the webhook's URL. */
export async function sendTestWebhookEvent(
  id: string,
  organizationId: string
): Promise<WebhookTestResponse> {
  return apiRequest<WebhookTestResponse>(
    `/webhooks/${id}/test`,
    {
      method: 'POST',
    },
    organizationId
  );
}

/** Get recent delivery attempts for a webhook. */
export async function getWebhookDeliveries(
  id: string,
  organizationId: string,
  limit = 50
): Promise<WebhookDeliveriesResponse> {
  return apiRequest<WebhookDeliveriesResponse>(
    `/webhooks/${id}/deliveries?limit=${limit}`,
    {},
    organizationId
  );
}

/**
 * Regenerate a webhook's signing secret.
 * Returns the new secret — SAVE IT, this is the only time it's shown.
 */
export async function regenerateWebhookSecret(
  id: string,
  organizationId: string
): Promise<WebhookRegenerateSecretResponse> {
  return apiRequest<WebhookRegenerateSecretResponse>(
    `/webhooks/${id}/regenerate-secret`,
    {
      method: 'POST',
    },
    organizationId
  );
}

// Re-export types for convenience
export type { Webhook, WebhookDelivery, CreateWebhookRequest, UpdateWebhookRequest };
